import { cloneElement, ReactElement, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Close from "@mui/icons-material/Close";
import CreateNewFolderOutlined from "@mui/icons-material/CreateNewFolderOutlined";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  OutlinedInput,
  Tooltip,
  Typography as T,
} from "@mui/material";
import useFiles, { useCurrentKey } from "files/hooks/useFiles";
import FileType from "files/types/enums/fileType";
import rumClient from "~/utils/monitoring";

const { VITE_RWB_API } = import.meta.env;

const ModalBody = (props: { onClose: () => void; prefix?: string }) => {
  const { data: filesData, mutate: mutateFiles } = useFiles(props.prefix);
  const [error, setError] = useState(false);
  const routeKey = useCurrentKey();
  // Prefer an explicit prefix (routes that useBasePath doesn't recognize, e.g. nextgen) and
  // fall back to deriving the current directory from the route (classic / workbench).
  const currentKey = props.prefix ?? routeKey;
  const form = useForm({
    mode: "onChange",
    defaultValues: { folderName: "" },
  });

  const onSubmit = async (data: { folderName: string }) => {
    setError(false);

    try {
      const folderPath = `${currentKey}${data.folderName}`;

      const response = await fetch(`${VITE_RWB_API}/file-browser/create-folder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          folder_path: folderPath,
        }),
      });

      if (!response.ok) {
        throw new Error(`Unable to create folder: ${response.status}`);
      }

      await mutateFiles();
      props.onClose();
    } catch (e) {
      setError(true);
      console.error(e);
      rumClient?.recordError(e);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <DialogTitle>
        New Folder
        <IconButton size="small" onClick={props.onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Unable to create folder
          </Alert>
        )}
        <FormControl>
          <Controller
            control={form.control}
            name="folderName"
            rules={{
              required: { value: true, message: "Required" },
              validate: {
                validName: (value: string) => {
                  if (value.includes("/")) {
                    return 'Folder name cannot contain "/"';
                  }
                },
                uniqueName: (value: string) => {
                  // Get keys of existing subdirectories
                  const siblings = filesData?.children
                    ?.filter((file) => file.type === FileType.DIRECTORY)
                    .map((file) => file.key);

                  const objectKey = `${filesData?.current_file?.key}${value}/`;

                  if (siblings?.includes(objectKey)) {
                    return "Folder already exists";
                  }
                },
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <FormLabel htmlFor={field.name}>
                  <T variant="label">Folder name *</T>
                </FormLabel>
                <OutlinedInput
                  autoFocus
                  autoComplete="off"
                  id={field.name}
                  size="small"
                  error={!!error}
                  {...field}
                />
                <FormHelperText error>{error?.message ?? " "}</FormHelperText>
              </>
            )}
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} size="medium" color="inherit" variant="outlined">
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          disabled={!form.formState.isValid}
          loading={form.formState.isSubmitting}
          variant="contained"
          size="medium"
        >
          Create
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

const CreateFolderDialog = (props: { open: boolean; onClose: () => void; prefix?: string }) => (
  <Dialog
    fullWidth
    open={props.open}
    onClose={props.onClose}
    /* Workaround to handle autoFocus in dev mode */
    disableRestoreFocus={import.meta.env.DEV}
  >
    <ModalBody onClose={props.onClose} prefix={props.prefix} />
  </Dialog>
);

export default CreateFolderDialog;
